import { Component, OnInit } from '@angular/core';
import { RestService } from 'app/services/rest/rest.service';

interface DashboardKpi {
  title: string;
  value: string;
  unit?: string | null;
  trend?: string | null;
  trendDirection?: string | null;
}

interface DashboardChart {
  title: string;
  labels?: string[];
  datasets?: Array<{ label?: string; data?: number[] }>;
  subtitle?: string | null;
  footer?: string | null;
  type?: 'line' | 'bar' | 'doughnut';
}

interface DashboardKpiCard extends DashboardKpi {
  icon: string;
  iconColor: string;
}

interface DashboardKpiGroup {
  title: string;
  tone: 'billing' | 'volume' | 'discounts' | 'neutral';
  items: DashboardKpiCard[];
}

type DashboardKpiTone = DashboardKpiGroup['tone'];
type DashboardKpiGroupKey = 'reserve' | 'occupation' | 'discounts' | 'other';

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.page.html',
  styleUrls: ['./admin-dashboard.page.scss'],
})
export class AdminDashboardPage implements OnInit {
  readonly kpiIcons = ['today', 'event_note', 'payments', 'upcoming', 'confirmation_number', 'percent', 'storefront', 'price_check', 'sell'];
  readonly kpiIconColorsByTone: Record<DashboardKpiTone, string[]> = {
    billing: ['#fb923c', '#f97316', '#ea580c', '#fdba74', '#c2410c'],
    volume: ['#2dd4bf', '#14b8a6', '#0f766e', '#5eead4', '#0d9488'],
    discounts: ['#a78bfa', '#8b5cf6', '#7c3aed', '#c4b5fd', '#6d28d9'],
    neutral: ['#94a3b8', '#64748b', '#475569', '#cbd5e1', '#334155']
  };
  readonly kpiGroupConfig: Record<DashboardKpiGroupKey, { title: string; tone: DashboardKpiTone }> = {
    reserve: { title: 'dashboard.group.billing', tone: 'billing' },
    occupation: { title: 'dashboard.group.volume', tone: 'volume' },
    discounts: { title: 'dashboard.group.discounts', tone: 'discounts' },
    other: { title: 'menu.admin.dashboard', tone: 'neutral' }
  };
  dashboard!: any;
  dashboardKpiGroups: DashboardKpiGroup[] = [];
  dashboardCharts: DashboardChart[] = [];

  constructor(
    private restService: RestService) { }

  ngOnInit() {
    this.loadDashboard();
  }

  loadDashboard() {
    this.restService.dashboardAnalytics().subscribe(res => {
      this.dashboard = res;
      this.dashboardCharts = this.buildDashboardCharts(res);
      this.dashboardKpiGroups = this.buildDashboardKpiGroups(res);
    });
  }

  getKpiIcon(index: number): string {
    return this.kpiIcons[index % this.kpiIcons.length];
  }

  getKpiIconColor(tone: DashboardKpiTone, index: number): string {
    const palette = this.kpiIconColorsByTone[tone];
    return palette[index % palette.length];
  }

  private buildDashboardKpiGroups(dashboard: any): DashboardKpiGroup[] {
    const groups = ((dashboard?.kpis || []) as DashboardKpi[]).reduce((acc, kpi, index) => {
      const groupKey = this.resolveKpiGroupKey(kpi.title);
      const groupConfig = this.kpiGroupConfig[groupKey];
      const group = acc[groupKey] || {
        title: groupConfig.title,
        tone: groupConfig.tone,
        items: []
      };

      group.items.push({
        ...kpi,
        value: this.formatKpiValue(kpi),
        icon: this.resolveKpiIcon(kpi.title, groupKey, group.items.length + index),
        iconColor: this.getKpiIconColor(group.tone, group.items.length)
      });

      acc[groupKey] = group;
      return acc;
    }, {} as Record<DashboardKpiGroupKey, DashboardKpiGroup>);

    return (['reserve', 'occupation', 'discounts', 'other'] as DashboardKpiGroupKey[])
      .map((groupKey) => groups[groupKey])
      .filter((group): group is DashboardKpiGroup => !!group && group.items.length > 0);
  }

  private buildDashboardCharts(dashboard: any): DashboardChart[] {
    return (((dashboard?.charts || []) as DashboardChart[])).map((chart) => ({
      ...chart,
      type: chart.type || 'line'
    }));
  }

  private resolveKpiGroupKey(title: string): DashboardKpiGroupKey {
    if (!title) {
      return 'other';
    }

    if (title.startsWith('reserve.')) {
      return 'reserve';
    }

    if (title.startsWith('occupation.')) {
      return 'occupation';
    }

    if (title.startsWith('discounts.')) {
      return 'discounts';
    }

    return 'other';
  }

  private resolveKpiIcon(title: string, groupKey: DashboardKpiGroupKey, index: number): string {
    const iconMap: Record<string, string> = {
      'reserve.kpis.today.sumAmount': 'today',
      'reserve.kpis.lastSevenDays.sumAmount': 'date_range',
      'reserve.kpis.thisMonth.sumAmount': 'payments',
      'reserve.kpis.future.sumAmount': 'upcoming',
      'occupation.kpis.average.ratioOccupationAvg': 'analytics',
      'occupation.kpis.daily.countReservations': 'event_available',
      'occupation.kpis.monthly.countReservations': 'calendar_month',
      'discounts.kpis.amount.countDiscount': 'sell'
    };

    if (iconMap[title]) {
      return iconMap[title];
    }

    const fallbackIconsByGroup: Record<DashboardKpiGroupKey, string[]> = {
      reserve: ['today', 'payments', 'price_check', 'request_quote'],
      occupation: ['analytics', 'event_available', 'calendar_month', 'query_stats'],
      discounts: ['sell', 'confirmation_number', 'local_offer', 'redeem'],
      other: this.kpiIcons
    };

    const fallbackIcons = fallbackIconsByGroup[groupKey];
    return fallbackIcons[index % fallbackIcons.length];
  }

  private formatKpiValue(kpi: DashboardKpi): string {
    if (!kpi) {
      return '0';
    }

    if (kpi.unit) {
      return `${kpi.value} ${kpi.unit}`.trim();
    }

    return kpi.value || '0';
  }
}
